import { useRef, useState, useCallback, useEffect } from 'react'

const MUSIC_URL =
  'https://ia800500.us.archive.org/5/items/JazzPiano_201610/JazzPiano.mp3'
const BASE_VOLUME = 0.4
const DUCKED_VOLUME = 0.0
const FADE_DURATION = 800

export function useBackgroundMusic() {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const fadeIntervalRef = useRef<number | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const startedRef = useRef(false)
  const initializedRef = useRef(false)

  const fadeTo = useCallback(
    (targetVolume: number, duration: number = FADE_DURATION) => {
      const audio = audioRef.current
      if (!audio) return

      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current)

      const startVolume = audio.volume
      const diff = targetVolume - startVolume
      const steps = 20
      const stepTime = duration / steps
      let currentStep = 0

      fadeIntervalRef.current = window.setInterval(() => {
        currentStep++
        const progress = currentStep / steps
        const eased = 1 - Math.pow(1 - progress, 3)
        audio.volume = Math.max(0, Math.min(1, startVolume + diff * eased))

        if (currentStep >= steps) {
          if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current)
          audio.volume = targetVolume
        }
      }, stepTime) as unknown as number
    },
    [],
  )

  const startMusic = useCallback(async () => {
    const audio = audioRef.current
    if (!audio || startedRef.current) return
    try {
      audio.volume = 0
      await audio.play()
      startedRef.current = true
      fadeTo(BASE_VOLUME, 2000)
      setIsPlaying(true)
    } catch (e) {
      // Autoplay blocked, will retry on user interaction
    }
  }, [fadeTo])

  // Defer audio initialization until after page is interactive
  const initAudio = useCallback(() => {
    if (initializedRef.current) return
    initializedRef.current = true

    const audio = new Audio(MUSIC_URL)
    audio.loop = true
    audio.volume = 0
    audio.preload = 'auto'
    audioRef.current = audio

    audio.addEventListener(
      'canplaythrough',
      () => {
        startMusic()
      },
      { once: true },
    )

    // Also try starting immediately in case it's cached
    startMusic()
  }, [startMusic])

  useEffect(() => {
    // Wait until page is loaded before initializing audio
    const timer = setTimeout(() => {
      initAudio()
    }, 2000)

    // Fallback: start on first user interaction
    const startOnInteraction = () => {
      initAudio()
      setTimeout(() => startMusic(), 100)
    }

    document.addEventListener('click', startOnInteraction, { once: true })
    document.addEventListener('touchstart', startOnInteraction, { once: true })
    document.addEventListener('scroll', startOnInteraction, { once: true })

    return () => {
      clearTimeout(timer)
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
      }
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current)
      document.removeEventListener('click', startOnInteraction)
      document.removeEventListener('touchstart', startOnInteraction)
      document.removeEventListener('scroll', startOnInteraction)
    }
  }, [initAudio, startMusic])

  const toggle = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      fadeTo(0, 600)
      setTimeout(() => {
        audio.pause()
        setIsPlaying(false)
      }, 650)
    } else {
      audio.volume = 0
      audio
        .play()
        .then(() => {
          fadeTo(BASE_VOLUME, 1200)
          setIsPlaying(true)
        })
        .catch(() => {})
    }
  }, [isPlaying, fadeTo])

  const duck = useCallback(() => {
    if (!startedRef.current) return
    fadeTo(DUCKED_VOLUME, 600)
  }, [fadeTo])

  const unduck = useCallback(() => {
    if (!startedRef.current) return
    const audio = audioRef.current
    if (audio && audio.paused) {
      audio
        .play()
        .then(() => {
          fadeTo(BASE_VOLUME, 1000)
        })
        .catch(() => {})
    } else {
      fadeTo(BASE_VOLUME, 1000)
    }
  }, [fadeTo])

  return { isPlaying, toggle, duck, unduck }
}
